import { API_BASE_URL, TOKEN_STORAGE_KEYS } from '../config/api';
import ApiError from './ApiError';

// Set by AuthProvider on mount so a background 401-refresh failure can clear
// the session and notify the UI without api/client.js importing React context
// (this module stays a plain, framework-agnostic fetch wrapper).
let onSessionExpired = () => {};
export const setOnSessionExpired = (fn) => {
  onSessionExpired = typeof fn === 'function' ? fn : () => {};
};

export const getStoredTokens = () => ({
  accessToken: localStorage.getItem(TOKEN_STORAGE_KEYS.access),
  refreshToken: localStorage.getItem(TOKEN_STORAGE_KEYS.refresh),
});

// The LOGIN session the stored tokens belong to: a random id created at
// sign-in, kept across token refreshes, removed at sign-out. Every request
// remembers the session it was sent in, so a 401 that arrives after a
// sign-out/sign-in (another account, or the same account signing in again)
// is never refreshed or replayed with the newer session's credentials. Kept
// beside the tokens, so every tab sees the same session. Not a credential.
export const AUTH_SESSION_STORAGE_KEY = 'fnh_auth_session';
const newSessionId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
const currentSessionId = () => {
  const session = localStorage.getItem(AUTH_SESSION_STORAGE_KEY);
  if (session || !localStorage.getItem(TOKEN_STORAGE_KEYS.access)) return session;
  // Tokens stored before sessions were tracked: they are one session.
  const adopted = newSessionId();
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, adopted);
  return adopted;
};

/** The active login session's id (null when signed out). Not a credential. */
export const getCurrentSessionId = () => currentSessionId();

const writeTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) localStorage.setItem(TOKEN_STORAGE_KEYS.access, accessToken);
  if (refreshToken) localStorage.setItem(TOKEN_STORAGE_KEYS.refresh, refreshToken);
};

/** Signing in: these tokens start a NEW login session. */
export const storeTokens = (tokens) => {
  localStorage.setItem(AUTH_SESSION_STORAGE_KEY, newSessionId());
  writeTokens(tokens);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_STORAGE_KEYS.access);
  localStorage.removeItem(TOKEN_STORAGE_KEYS.refresh);
  localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
};

const sessionChanged = () => new ApiError({ message: 'Your session changed while this was loading.', code: 'SESSION_CHANGED', status: 401 });

// ── Offer channel ─────────────────────────────────────────────────────────
// Offers are configured per sales channel (app, website, pos). Every request
// to the customer API identifies this storefront as the website so the
// backend applies website offers only. A constant, never user input: it
// selects which offers apply, it is not proof of who is calling.
export const CLIENT_CHANNEL_HEADER = 'X-Client-Channel';
export const CLIENT_CHANNEL = 'website';

// ── Delivery geography ────────────────────────────────────────────────────
// The browsing location travels as X-Delivery-* headers on every request so
// the backend can resolve the serving outlet itself. The client only ever
// sends geography — never an outlet id. DeliveryLocationContext is the single
// writer of the global value; a request may pin its own geography instead
// (e.g. a checkout address that differs from the browsing location).
export const DELIVERY_PINCODE_HEADER = 'X-Delivery-Pincode';
export const DELIVERY_LAT_HEADER = 'X-Delivery-Lat';
export const DELIVERY_LNG_HEADER = 'X-Delivery-Lng';

const PINCODE_PATTERN = /^[0-9]{6}$/;

const toCoordinate = (value, maximum) => {
  if (typeof value !== 'number' && !(typeof value === 'string' && value.trim())) return null;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= maximum ? number : null;
};

/**
 * Validates geography into `{ pincode, coordinates }`, or null when nothing
 * usable remains. The backend silently ignores a malformed header and falls
 * back to the customer's saved address, so invalid values are dropped here
 * rather than sent. Coordinates are a pair: both valid or neither.
 */
export const normalizeDeliveryGeography = (input) => {
  if (!input || typeof input !== 'object') return null;
  const rawPincode = typeof input.pincode === 'number' ? String(input.pincode) : input.pincode;
  const pincode = typeof rawPincode === 'string' && PINCODE_PATTERN.test(rawPincode.trim())
    ? rawPincode.trim()
    : null;
  const lat = toCoordinate(input.coordinates?.lat, 90);
  const lng = toCoordinate(input.coordinates?.lng, 180);
  const coordinates = lat !== null && lng !== null ? { lat, lng } : null;
  if (!pincode && !coordinates) return null;
  return { pincode, coordinates };
};

/** Stable identity of a geography, for change detection. '' means none. */
export const deliveryGeographyKey = (geography) => {
  const normalized = normalizeDeliveryGeography(geography);
  if (!normalized) return '';
  const { pincode, coordinates } = normalized;
  return `${pincode || ''}|${coordinates ? `${coordinates.lat},${coordinates.lng}` : ''}`;
};

let deliveryGeography = null;
// Bumped only when the effective geography changes, so opted-in requests can
// tell that the location moved while they were in flight.
let deliveryGeographyVersion = 0;

export const setDeliveryGeography = (input) => {
  const next = normalizeDeliveryGeography(input);
  if (deliveryGeographyKey(next) === deliveryGeographyKey(deliveryGeography)) return;
  deliveryGeography = next;
  deliveryGeographyVersion += 1;
};

export const getDeliveryGeography = () => (
  deliveryGeography
    ? { pincode: deliveryGeography.pincode, coordinates: deliveryGeography.coordinates && { ...deliveryGeography.coordinates } }
    : null
);

export const getDeliveryGeographyVersion = () => deliveryGeographyVersion;

const deliveryHeadersFor = (geography) => {
  const headers = {};
  if (!geography) return headers;
  if (geography.pincode) headers[DELIVERY_PINCODE_HEADER] = geography.pincode;
  if (geography.coordinates) {
    headers[DELIVERY_LAT_HEADER] = String(geography.coordinates.lat);
    headers[DELIVERY_LNG_HEADER] = String(geography.coordinates.lng);
  }
  return headers;
};

// De-duplicates concurrent refresh attempts triggered by multiple in-flight
// requests of one session all receiving a 401 at once.
let refresh = null; // { session, promise }

// Refreshes the given session's tokens. Its answer is stored only if that
// session is still the active one when it arrives.
const performRefresh = async (session) => {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) throw new ApiError({ message: 'No session to refresh', code: 'SESSION_EXPIRED' });

  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ refreshToken }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError({
      message: body?.message || 'Session expired',
      status: response.status,
      code: 'SESSION_EXPIRED',
    });
  }
  if (currentSessionId() !== session) throw sessionChanged();
  writeTokens({ accessToken: body?.data?.accessToken, refreshToken: body?.data?.refreshToken });
  return body?.data;
};

const buildUrl = (path, params, baseUrl = API_BASE_URL) => {
  const url = new URL(`${baseUrl}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, value);
    });
  }
  return url.toString();
};

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * Central request function used by every api/endpoints/* module.
 *
 * @param {string} path - appended to API_BASE_URL, e.g. '/categories'
 * @param {object} options
 * @param {string} [options.method='GET']
 * @param {object} [options.body] - JSON-serialized if present
 * @param {object} [options.params] - query string params
 * @param {boolean} [options.auth=true] - attach Authorization header
 * @param {string} [options.idempotencyKey] - sets the Idempotency-Key header
 * @param {object|null} [options.deliveryGeography] - pins this request's
 *   X-Delivery-* headers to `{ pincode, coordinates: { lat, lng } }` instead
 *   of the global browsing geography, without changing it. `null` sends no
 *   geography at all. Omit to use the global value.
 * @param {boolean} [options.geographySensitive] - reject the response with
 *   STALE_DELIVERY_GEOGRAPHY if the global geography changed while in flight.
 *   Only meaningful for requests that use the global geography.
 * @param {string} [options.baseUrl] - another backend mount (e.g. the Places
 *   proxy); defaults to API_BASE_URL
 * @param {boolean} [options._isRetry] - internal, prevents infinite 401 retry loops
 */
export const request = async (path, options = {}) => {
  const {
    method = 'GET',
    body,
    params,
    baseUrl,
    auth = true,
    idempotencyKey,
    geographySensitive = false,
    _isRetry = false,
    // A 401 retry is bound to the login session of the request it repeats.
    _session,
    // A caller may bind a request to the login session it started in: if
    // that session is no longer active, the request is never sent.
    session,
  } = options;

  // Resolved once per call. The 401 retry receives the exact geography (and
  // version) the first attempt used, so a location change during the token
  // refresh can never make one logical request span two locations.
  const pinned = _isRetry
    ? options._pinnedGeography === true
    : Object.prototype.hasOwnProperty.call(options, 'deliveryGeography');
  let geography;
  if (_isRetry) {
    geography = options._resolvedGeography ?? null;
  } else if (pinned) {
    geography = options.deliveryGeography === null ? null : normalizeDeliveryGeography(options.deliveryGeography);
    if (options.deliveryGeography !== null && !geography) {
      // Falling back to the browsing location here would silently price or
      // check a different place than the caller asked about.
      throw new ApiError({ message: 'Invalid delivery location.', code: 'INVALID_DELIVERY_GEOGRAPHY' });
    }
  } else {
    geography = deliveryGeography;
  }
  const sentGeographyVersion = options._geographyVersion ?? deliveryGeographyVersion;

  const headers = { 'Content-Type': 'application/json', ...deliveryHeadersFor(geography) };
  // Only the customer API prices offers; other mounts (the Places proxy) are
  // not told a channel they have no use for.
  if (!baseUrl) headers[CLIENT_CHANNEL_HEADER] = CLIENT_CHANNEL;
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
  let sentAuthHeader = false;
  let sentAccessToken = null;
  let sentSession = null;
  if (auth) {
    // Read as one step, right before the request leaves (nothing below
    // awaits before fetch): the session and the token it sends belong
    // together. A retry must still be in its ORIGINATING session; if that
    // session is gone it is never sent with whatever session is stored now.
    const active = currentSessionId();
    const expected = _isRetry ? _session : session;
    if (expected !== undefined && active !== expected) throw sessionChanged();
    const { accessToken } = getStoredTokens();
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
      sentAuthHeader = true;
      sentAccessToken = accessToken;
      sentSession = active;
    }
  }

  let response;
  try {
    response = await fetch(buildUrl(path, params, baseUrl), {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError({ message: 'Network error — please check your connection.', code: 'NETWORK_ERROR' });
  }

  // A guest request without an Authorization header has no session to
  // refresh. When a token was rejected, refresh once; a second 401 means the
  // refreshed credentials were also rejected and the local session is stale.
  if (response.status === 401 && sentAuthHeader) {
    // The LOGIN session this request was sent in must still be the active
    // one. If anyone signed out or in since — another account, or the same
    // account in a new session — the request is NOT refreshed and replayed
    // (that would run it as someone else), and the active session is left
    // exactly as it is. The caller just learns its request is void.
    if (currentSessionId() !== sentSession) throw sessionChanged();
    if (getStoredTokens().accessToken !== sentAccessToken && !_isRetry) {
      // Another request of this same session already refreshed: use its token.
      return request(path, {
        ...options,
        _resolvedGeography: geography,
        _pinnedGeography: pinned,
        _geographyVersion: sentGeographyVersion,
        _isRetry: true,
        _session: sentSession,
      });
    }
    if (_isRetry) {
      clearTokens();
      onSessionExpired();
      throw new ApiError({ message: 'Your session has expired. Please sign in again.', code: 'SESSION_EXPIRED', status: 401 });
    }
    try {
      if (!refresh || refresh.session !== sentSession) {
        const entry = { session: sentSession };
        entry.promise = performRefresh(sentSession).finally(() => { if (refresh === entry) refresh = null; });
        refresh = entry;
      }
      await refresh.promise;
    } catch (refreshErr) {
      // The session moved on while refreshing: nothing of it is touched.
      if (refreshErr?.code === 'SESSION_CHANGED' || currentSessionId() !== sentSession) throw sessionChanged();
      clearTokens();
      onSessionExpired();
      throw new ApiError({ message: 'Your session has expired. Please sign in again.', code: 'SESSION_EXPIRED', status: 401 });
    }
    // The refresh is done; the session may have moved on while it ran.
    if (currentSessionId() !== sentSession) throw sessionChanged();
    return request(path, {
      ...options,
      _resolvedGeography: geography,
      _pinnedGeography: pinned,
      _geographyVersion: sentGeographyVersion,
      _isRetry: true,
      _session: sentSession,
    });
  }

  if (geographySensitive && !pinned && sentGeographyVersion !== deliveryGeographyVersion) {
    throw new ApiError({
      message: 'Your delivery location changed while this was loading.',
      code: 'STALE_DELIVERY_GEOGRAPHY',
    });
  }

  const data = await parseBody(response);

  if (!response.ok) {
    const retryAfter = Number(response.headers?.get?.('Retry-After'));
    throw new ApiError({
      message: data?.message || `Request failed (${response.status})`,
      status: response.status,
      code: data?.code || null,
      details: data,
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter >= 0 ? retryAfter : null,
    });
  }

  return data;
};

const apiClient = {
  request,
  setOnSessionExpired,
  getStoredTokens,
  storeTokens,
  clearTokens,
  setDeliveryGeography,
  getDeliveryGeography,
  getDeliveryGeographyVersion,
};
export default apiClient;
